import type {
  ReferrerStatus,
  ReferrerSummary,
} from "@redotech/admin-sdk/rpc/schema/referrers/shared";
import { useRequiredContext } from "@redotech/react-util/context";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useCallback, useState } from "react";

import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { mapReferrerErrorToFieldError } from "../lib/map-referrer-error-to-field-errors";

interface ReferrerActionPanelProps {
  referrer: ReferrerSummary;
  /** Called after any action that mutates the referrer so the detail page refetches. */
  onReferrerChanged: () => void;
}

type PendingAction = "pause" | "reactivate" | "terminate" | null;

export const ReferrerActionPanel = memo(function ReferrerActionPanel({
  referrer,
  onReferrerChanged,
}: ReferrerActionPanelProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingAction>(null);
  const [submitting, setSubmitting] = useState(false);

  const isTerminated = referrer.status === "terminated";
  const isPaused = referrer.status === "paused";
  const isActive = referrer.status === "active";

  const handleResend = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await rpcClient.resendMagicLink({
        referrerId: referrer.id,
      });
      if (result.magicLinkSent) {
        toast("Magic link sent.", { variant: "success" });
      } else {
        toast("Magic link delivery failed. Try again later.", {
          variant: "warning",
        });
      }
      onReferrerChanged();
    } catch (err) {
      const mapping = mapReferrerErrorToFieldError(err);
      toast(
        mapping?.message ??
          (err instanceof Error ? err.message : "Failed to resend magic link."),
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  }, [rpcClient, referrer.id, onReferrerChanged]);

  const handleStatusChange = useCallback(
    async (status: ReferrerStatus) => {
      setSubmitting(true);
      try {
        await rpcClient.updateReferrer({ referrerId: referrer.id, status });
        toast(
          status === "active"
            ? "Referrer reactivated."
            : status === "paused"
              ? "Referrer paused."
              : "Referrer terminated.",
          { variant: "success" },
        );
        onReferrerChanged();
      } catch (err) {
        const mapping = mapReferrerErrorToFieldError(err);
        toast(
          mapping?.message ??
            (err instanceof Error
              ? err.message
              : "Failed to update referrer status."),
          { variant: "error" },
        );
      } finally {
        setSubmitting(false);
        setPendingConfirmation(null);
      }
    },
    [rpcClient, referrer.id, onReferrerChanged],
  );

  return (
    <Flex align="center" dir="row" gap="sm">
      <RedoButton
        disabled={isTerminated || submitting}
        hierarchy="secondary"
        onClick={() => void handleResend()}
        size="sm"
        text="Resend magic link"
      />
      {isActive && (
        <RedoButton
          disabled={submitting}
          hierarchy="secondary"
          onClick={() => setPendingConfirmation("pause")}
          size="sm"
          text="Pause"
        />
      )}
      {isPaused && (
        <RedoButton
          disabled={submitting}
          hierarchy="secondary"
          onClick={() => setPendingConfirmation("reactivate")}
          size="sm"
          text="Reactivate"
        />
      )}
      <RedoButton
        disabled={isTerminated || submitting}
        hierarchy="secondary"
        onClick={() => setPendingConfirmation("terminate")}
        size="sm"
        text="Terminate"
      />

      {pendingConfirmation === "pause" && (
        <RedoModal
          onModalCloseRequested={() => setPendingConfirmation(null)}
          primaryButton={{
            text: submitting ? "Pausing…" : "Pause referrer",
            onClick: () => void handleStatusChange("paused"),
            pending: submitting,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setPendingConfirmation(null),
            disabled: submitting,
          }}
          subtitle="The referrer will lose login access until reactivated. Pausing is reversible."
          title="Pause referrer?"
        />
      )}

      {pendingConfirmation === "reactivate" && (
        <RedoModal
          onModalCloseRequested={() => setPendingConfirmation(null)}
          primaryButton={{
            text: submitting ? "Reactivating…" : "Reactivate referrer",
            onClick: () => void handleStatusChange("active"),
            pending: submitting,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setPendingConfirmation(null),
            disabled: submitting,
          }}
          subtitle="The referrer will regain login access immediately."
          title="Reactivate referrer?"
        />
      )}

      {pendingConfirmation === "terminate" && (
        <RedoModal
          onModalCloseRequested={() => setPendingConfirmation(null)}
          primaryButton={{
            text: submitting ? "Terminating…" : "Terminate referrer",
            onClick: () => void handleStatusChange("terminated"),
            pending: submitting,
            theme: "destructive",
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setPendingConfirmation(null),
            disabled: submitting,
          }}
          subtitle="Termination is permanent. Existing referrer sessions are revoked on the next request and the referrer can't be reactivated through this UI."
          theme="warn"
          title="Terminate referrer?"
        />
      )}
    </Flex>
  );
});
